import { Box } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Input from "@ui/input/Input";
import Spacer from "@ui/spacer/Spacer";
import { Typo, TypoCaption } from "@ui/text/Typo";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GD } from "src/GD";
import { getFriendlyErrorMessage } from "src/utils/errors";
import { isValidEmail, normalizeEmail } from "src/utils/validation";
import { FormParser } from "src/view/FormParser";
import { StartScreenView } from "../StartScreenView";
import { DS } from "../ds/Kit";
import {
	AUTH_FORM_ACTIONS_SX,
	AUTH_PRIMARY_BUTTON_SX,
	AUTH_SECONDARY_BUTTON_SX,
	AUTH_TWO_COLUMN_SX,
	AuthCard,
	AuthDivider,
	AuthStatus,
} from "./AuthLayout";
import { S_AUTH_CHANGE_VIEW } from "./AuthView";

// Client-side mirror of the backend resend throttle: 60s after the first
// letter, 5 minutes for every subsequent one.
const FIRST_RESEND_COOLDOWN = 60;
const NEXT_RESEND_COOLDOWN = 5 * 60;
const REFERRAL_CODE_LENGTH = 8;
const REFERRAL_CODE_MAX_LENGTH = 16;
const REFERRAL_CHECK_DEBOUNCE_MS = 400;

const responsiveFields: RecursiveCSS = {
	...AUTH_TWO_COLUMN_SX,
	alignItems: "start",
};

const getForm = () => ({
	name: "",
	$__name_label: "Full name",
	$__name_autoComplete: "name",
	email: "",
	$__email_type: "email",
	$__email_autoComplete: "email",
	password: "",
	$__password_type: "password",
	$__password_autoComplete: "new-password",
	pass_confirm: "",
	$__pass_confirm_type: "password",
	$__pass_confirm_autoComplete: "new-password",
	$__pass_confirm_label: "Confirm password",
	$__pass_confirm_details: "Repeat password",
	$__decorator: {
		blocks: [
			{ order: ["name", "email"], blockless: true, sx: responsiveFields },
			{ order: ["password", "pass_confirm"], blockless: true, sx: responsiveFields },
		],
	},
});

const getCorporateForm = () => ({
	company_name: "",
	$__company_name_label: "Company name",
	registration_number: "",
	$__registration_number_label: "Registration number",
	$__decorator: {
		blocks: [
			{ order: ["company_name", "registration_number"], blockless: true, sx: responsiveFields },
		],
	},
});

type AccountType = "private" | "corporate";
type SignupStep = "account-type" | "company" | "credentials";
type CorporateDetails = {
	company_name: string;
	registration_number: string;
};

const AccountOption = ({
	title,
	description,
	meta,
	icon,
	onClick,
}: {
	title: string;
	description: string;
	meta: string;
	icon: ReactNode;
	onClick: () => void;
}) => (
	<Box
		element="button"
		type="button"
		aria-label={title}
		onClick={onClick}
		sx={{
			minWidth: 0,
			minHeight: "156px",
			padding: "20px",
			display: "flex",
			flexDirection: "column",
			textAlign: "left",
			font: "inherit",
			color: "inherit",
			cursor: "pointer",
			border: `1px solid ${DS.line}`,
			borderRadius: DS.radius,
			backgroundColor: DS.surface,
			boxShadow: DS.shadow,
			transition: "border-color .18s ease, box-shadow .18s ease, transform .18s ease",
			"&:hover": {
				borderColor: DS.accent,
				boxShadow: "0 10px 24px rgba(30, 58, 95, .14)",
				transform: "translateY(-1px)",
			} as RecursiveCSS,
			"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "2px" } as RecursiveCSS,
		}}>
		<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
			<Box aria-hidden="true" sx={{ width: "44px", height: "44px", borderRadius: "13px", display: "grid", placeItems: "center", backgroundColor: DS.accentSoft, color: DS.accent }}>
				<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icon}</svg>
			</Box>
			<Box sx={{ flex: "0 0 auto", padding: "3px 10px", borderRadius: "999px", backgroundColor: "#ECFDF5", color: "#047857", fontSize: "12px", lineHeight: 1.4, fontWeight: 600 }}>
				{meta}
			</Box>
		</Box>
		<Typo sx={{ marginTop: "16px", color: DS.ink, fontSize: "17px", lineHeight: 1.3, fontWeight: 700 }}>{title}</Typo>
		<Typo sx={{ marginTop: "6px", color: DS.muted, fontSize: "14px", lineHeight: 1.5 }}>{description}</Typo>
		<Box sx={{ marginTop: "auto", paddingTop: "14px", color: DS.accent, fontSize: "13px", lineHeight: 1.3, fontWeight: 600 }}>
			Select →
		</Box>
	</Box>
);

const RegistrationSent = ({ email }: { email: string }) => {
	const [cooldown, setCooldown] = useState(FIRST_RESEND_COOLDOWN);
	const [resendCount, setResendCount] = useState(0);
	const [busy, setBusy] = useState(false);
	const timer = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		timer.current = setInterval(() => setCooldown(value => (value > 0 ? value - 1 : 0)), 1000);
		return () => {
			if (timer.current) clearInterval(timer.current);
		};
	}, []);

	const resend = useCallback(async () => {
		if (cooldown > 0 || busy) return;
		setBusy(true);
		const res = await GD.REQ_AUTH_REGISTER_RESEND.request({ email });
		setBusy(false);

		if (res.error) {
			toast.error(getFriendlyErrorMessage(res.error, "Could not resend the email"));
			return;
		}
		toast.success("Confirmation email sent");
		setResendCount(count => count + 1);
		setCooldown(NEXT_RESEND_COOLDOWN);
	}, [cooldown, busy, email]);

	return (
		<AuthCard icon="mail" eyebrow="Account created" title="Check your email" subtitle="Confirm your email address before signing in to Cardpartner.">
			<AuthStatus tone="success" title="Confirmation link sent">
				We sent the link to <b>{email}</b>. It expires in 24 hours.
			</AuthStatus>
			<Spacer size="22px" />
			<Button
				title={busy ? "Sending..." : cooldown > 0 ? `Resend email (${cooldown}s)` : "Resend email"}
				variant="primary"
				disabled={busy || cooldown > 0}
				onClick={resend}
				sx={AUTH_PRIMARY_BUTTON_SX}
			/>
			{resendCount > 0 ? (
				<TypoCaption sx={{ marginTop: "10px", color: DS.muted, textAlign: "center", lineHeight: 1.5 }}>
					Still waiting? Check your spam folder.
				</TypoCaption>
			) : null}
			<AuthDivider />
			<Button title="Back to sign in" variant="secondary" onClick={() => S_AUTH_CHANGE_VIEW.invoke("login")} sx={AUTH_SECONDARY_BUTTON_SX} />
		</AuthCard>
	);
};

export const AuthSignup = () => {
	const [busy, setBusy] = useState(false);
	const [sentTo, setSentTo] = useState<string | null>(null);
	const [formData] = useState(getForm());
	const [companyForm] = useState(getCorporateForm());
	const [accountType, setAccountType] = useState<AccountType | null>(null);
	const [step, setStep] = useState<SignupStep>("account-type");
	const [corporateDetails, setCorporateDetails] = useState<CorporateDetails | null>(null);
	const [referral, setReferral] = useState(() => ({
		code: new URLSearchParams(window.location.search).get("referral_code")?.trim().toUpperCase() ?? "",
	}));
	const [referralState, setReferralState] = useState<"checking" | "valid" | "invalid" | "error" | null>(null);
	const [referringCompany, setReferringCompany] = useState<string | null>(null);
	const referralRequest = useRef(0);

	const openPrivateRegistration = () => {
		setAccountType("private");
		setStep("credentials");
	};

	const openCorporateRegistration = () => {
		setAccountType("corporate");
		setStep("company");
	};

	const validateReferral = useCallback(async () => {
		const code = referral.code.trim().toUpperCase();
		if (!code) {
			setReferralState(null);
			setReferringCompany(null);
			return true;
		}
		const request = ++referralRequest.current;
		setReferralState("checking");
		const response = await GD.REQ_REFERRAL_CHECK.request({ code });
		if (request !== referralRequest.current) return false;
		if (response.error) {
			setReferralState("error");
			setReferringCompany(null);
			return false;
		}
		const valid = response.data.valid === true;
		setReferralState(valid ? "valid" : "invalid");
		setReferringCompany(valid ? response.data.company_name?.trim() || null : null);
		return valid;
	}, [referral.code]);

	useEffect(() => {
		const code = referral.code.trim();
		if (code.length !== REFERRAL_CODE_LENGTH) return;
		const timer = setTimeout(() => void validateReferral(), REFERRAL_CHECK_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [referral.code, validateReferral]);

	if (sentTo) {
		return <StartScreenView><RegistrationSent email={sentTo} /></StartScreenView>;
	}

	if (step === "account-type") {
		return (
			<StartScreenView>
				<AuthCard size="wide" eyebrow="New workspace account" title="Create your account" subtitle="Choose the access profile that matches how you will use Cardpartner.">
					<Box sx={AUTH_TWO_COLUMN_SX}>
						<AccountOption title="Private account" description="For individual access to your own cards, funding, and transaction workspace." meta="Available" icon={<><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" /></>} onClick={openPrivateRegistration} />
						<AccountOption title="Corporate account" description="For company-managed teams, roles, and operational accounts." meta="Available" icon={<><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M9 8h.01M15 8h.01M9 12h.01M15 12h.01M10 21v-4h4v4" /></>} onClick={openCorporateRegistration} />
					</Box>
					<AuthDivider label="Already registered?" />
					<Button title="Sign in" variant="secondary" onClick={() => S_AUTH_CHANGE_VIEW.invoke("login")} sx={AUTH_SECONDARY_BUTTON_SX} />
				</AuthCard>
			</StartScreenView>
		);
	}

	if (step === "company") {
		return (
			<StartScreenView>
				<AuthCard size="wide" eyebrow="Company profile" title="Tell us about your company" subtitle="These details identify the legal entity that will own the workspace.">
					<FormParser
						key="corporateDetailsForm"
						ignoreObserver={true}
						fullSizeButtons={true}
						actionsSx={AUTH_FORM_ACTIONS_SX}
						secondaryButton={null}
						primaryButton={{ title: "Continue", variant: "primary", sx: AUTH_PRIMARY_BUTTON_SX }}
						onSubmit={async (data: CorporateDetails) => {
							if (!data.company_name?.trim()) return { data, error: { code: 0, message: "Company name is required" } };
							if (!data.registration_number?.trim()) return { data, error: { code: 0, message: "Registration number is required" } };

							setCorporateDetails({
								company_name: data.company_name.trim(),
								registration_number: data.registration_number.trim(),
							});
							setStep("credentials");
							return { data };
						}}
						onRequest={async () => companyForm}
					/>
					<AuthDivider />
					<Box sx={AUTH_TWO_COLUMN_SX}>
						<Button title="Back" variant="secondary" onClick={() => setStep("account-type")} sx={AUTH_SECONDARY_BUTTON_SX} />
						<Button title="Sign in" variant="secondary" onClick={() => S_AUTH_CHANGE_VIEW.invoke("login")} sx={AUTH_SECONDARY_BUTTON_SX} />
					</Box>
				</AuthCard>
			</StartScreenView>
		);
	}

	return (
		<StartScreenView busy={busy}>
			<AuthCard
				size="wide"
				eyebrow={accountType === "corporate" ? "Company administrator" : "Private account"}
				title="Set up your credentials"
				subtitle="Use an active email address. You will confirm it before your first sign in.">
				<Box sx={{ marginBottom: "18px", padding: "16px", border: `1px solid ${referralState === "invalid" ? "#FECACA" : DS.line}`, borderRadius: DS.radius, backgroundColor: referralState === "invalid" ? "#FEF2F2" : DS.soft }}>
					<Typo sx={{ color: DS.ink, fontSize: "14px", fontWeight: 700 }}>Have an invitation? <span style={{ color: DS.muted, fontWeight: 500 }}>(optional)</span></Typo>
					<Spacer size="10px" />
					<Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: "10px", alignItems: "end", "@media (max-width: 520px)": { gridTemplateColumns: "minmax(0, 1fr)" } }}>
						<Input
							data={referral}
							name="code"
							label="Invitation code"
							maxLength={REFERRAL_CODE_MAX_LENGTH}
							onBloorProps={() => referral.code && void validateReferral()}
							onChange={value => {
								referralRequest.current++;
								setReferral({ code: value.toUpperCase().replace(/\s+/g, "") });
								setReferralState(null);
								setReferringCompany(null);
							}}
						/>
						<Button
							title={referralState === "checking" ? "Checking…" : "Check"}
							variant="secondary"
							disabled={!referral.code || referralState === "checking"}
							onClick={() => void validateReferral()}
							sx={{ ...AUTH_SECONDARY_BUTTON_SX, minWidth: "126px" }}
						/>
					</Box>
					{referralState === "valid" ? <><Spacer size="10px" /><AuthStatus tone="success">{referringCompany ? `You were invited by ${referringCompany}.` : "Invitation code confirmed."}</AuthStatus></> : null}
					{referralState === "invalid" ? <><Spacer size="10px" /><AuthStatus tone="error">This invitation code is not valid.</AuthStatus></> : null}
					{referralState === "error" ? <><Spacer size="10px" /><AuthStatus tone="error">Could not check this code. Try again.</AuthStatus></> : null}
				</Box>
				<FormParser
					key="signupForm"
					ignoreObserver={true}
					fullSizeButtons={true}
					actionsSx={AUTH_FORM_ACTIONS_SX}
					secondaryButton={null}
					primaryButton={{ title: "Create account", variant: "primary", sx: { ...AUTH_PRIMARY_BUTTON_SX, flexGrow: 1 } }}
					onSubmit={async (data: ReturnType<typeof getForm>) => {
						if (!data.name || data.name.trim().length < 2) {
							return { error: { code: 0, message: "Name must be at least 2 characters" }, data };
						}
						const email = normalizeEmail(data.email);
						if (!isValidEmail(email)) {
							return { error: { code: 0, message: "Enter a valid email address" }, data };
						}
						if ((data.password ?? "").length < 5) {
							return { error: { code: 0, message: "Password must be at least 5 characters" }, data };
						}
						if (data.password !== data.pass_confirm) {
							return { error: { code: 0, message: "Passwords do not match" }, data };
						}

						const referralCode = referral.code.trim().toUpperCase();
						if (referralCode && referralState !== "valid" && !(await validateReferral())) {
							return { data, error: { code: 2000, message: "Check the invitation code before creating the account" } };
						}
						if (accountType === "corporate" && !corporateDetails) {
							setStep("company");
							return { data, error: { code: 0, message: "Company details are required" } };
						}

						setBusy(true);
						let response: IAPIResponse;
						try {
							if (accountType === "corporate") {
								response = await GD.REQ_AUTH_REGISTER.request({
									name: data.name.trim(),
									email,
									password: data.password,
									type: "company",
									...(referralCode ? { referral_code: referralCode } : {}),
									...corporateDetails!,
								});
							} else {
								response = await GD.REQ_AUTH_REGISTER.request({
									name: data.name.trim(),
									email,
									password: data.password,
									type: "private",
									...(referralCode ? { referral_code: referralCode } : {}),
								});
							}
						} finally {
							setBusy(false);
						}

						if (response.error) {
							if ((response.error.details || response.error.message || "").toLowerCase().includes("referral code")) {
								setReferralState("invalid");
								setReferringCompany(null);
							}
							const message = response.error.code === 2800
								? "An account with this email already exists."
								: response.error.details || response.error.message || "Registration failed";
							return { data, error: { ...response.error, message } };
						}

						setSentTo(email);
						return { data };
					}}
					onRequest={async () => formData}
				/>

				<AuthDivider label="Already registered?" />
				<Box sx={accountType === "corporate" ? AUTH_TWO_COLUMN_SX : undefined}>
					{accountType === "corporate" ? (
						<Button title="Back" variant="secondary" onClick={() => setStep("company")} sx={AUTH_SECONDARY_BUTTON_SX} />
					) : null}
					<Button title="Sign in" variant="secondary" onClick={() => S_AUTH_CHANGE_VIEW.invoke("login")} sx={AUTH_SECONDARY_BUTTON_SX} />
				</Box>
			</AuthCard>
		</StartScreenView>
	);
};
